import { ref } from "vue";

/**
 * Tracks which row (identified by id) currently has an in-flight async action,
 * so a table can show a spinner on exactly that row's button.
 *
 * `run` flips the busy id on before awaiting the action and always clears it
 * afterwards (even if the action throws), replacing the repeated
 * `busyId = id; try { ... } finally { busyId = null }` boilerplate.
 *
 * @example
 * const completing = useRowAction();
 * const complete = (id: number) => completing.run(id, () => api.complete(id));
 * // template: :busy="completing.isBusy(transaction.id)"
 */
export function useRowAction() {
  const busyId = ref<number | null>(null);

  const isBusy = (id: number) => busyId.value === id;

  const run = async <T>(id: number, action: () => Promise<T>): Promise<T> => {
    busyId.value = id;
    try {
      return await action();
    } finally {
      busyId.value = null;
    }
  };

  return { isBusy, run };
}

/**
 * Tracks a single (non row-scoped) in-flight async action via a boolean flag.
 *
 * `run` flips the flag on before awaiting the action and always clears it
 * afterwards (even if the action throws).
 *
 * @example
 * const { busy: isReconciling, run: runReconcile } = useBusy();
 * const reconcile = () => runReconcile(() => api.reconcile());
 * // template: :busy="isReconciling"
 */
export function useBusy() {
  const busy = ref(false);

  const run = async <T>(action: () => Promise<T>): Promise<T> => {
    busy.value = true;
    try {
      return await action();
    } finally {
      busy.value = false;
    }
  };

  return { busy, run };
}
